import { useId, useState } from 'react'

import { WorkflowTimingAnchor } from '@/gen/cortado/v1/cortado_pb'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'

/**
 * Event-relative timing for WAIT nodes and task due dates. An offset is a
 * signed day count from the run's event start: -14 is two weeks before, 2 is
 * two days after. The backend bounds it to ±365 (MaxEventOffsetDays).
 */
export const MAX_EVENT_OFFSET_DAYS = 365

/** The offset a fresh "relative to the event" choice starts at. */
export const DEFAULT_EVENT_OFFSET_DAYS = -7

export function isEventStartAnchor(anchor: WorkflowTimingAnchor | undefined): boolean {
  return anchor === WorkflowTimingAnchor.EVENT_START
}

/** "14 days before the event", "1 day after the event", "the event start". */
export function describeEventOffset(offsetDays: number): string {
  if (offsetDays === 0) return 'the event start'
  const n = Math.abs(offsetDays)
  return `${n} ${n === 1 ? 'day' : 'days'} ${offsetDays < 0 ? 'before' : 'after'} the event`
}

type Direction = 'before' | 'after'

/**
 * EventOffsetFields edits a signed offset as "N days [before|after] the event
 * start". The direction is held locally so choosing "after" with 0 days is not
 * lost before a day count is typed.
 */
export function EventOffsetFields({
  offsetDays,
  onChange,
  daysLabel = 'Days',
}: {
  offsetDays: number
  onChange: (offsetDays: number) => void
  daysLabel?: string
}) {
  const daysId = useId()
  const directionId = useId()
  const [direction, setDirection] = useState<Direction>(offsetDays > 0 ? 'after' : 'before')
  const amount = Math.abs(offsetDays)

  const emit = (nextAmount: number, nextDirection: Direction) => {
    const clamped = Math.min(Math.max(0, Math.trunc(nextAmount)), MAX_EVENT_OFFSET_DAYS)
    onChange(nextDirection === 'before' ? -clamped : clamped)
  }

  return (
    <div className="grid gap-5 @[320px]:grid-cols-[minmax(0,0.7fr)_minmax(140px,1fr)]">
      <div className="grid gap-2">
        <Label htmlFor={daysId}>{daysLabel}</Label>
        <Input
          id={daysId}
          type="number"
          min={0}
          max={MAX_EVENT_OFFSET_DAYS}
          step={1}
          inputMode="numeric"
          value={String(amount)}
          onChange={(event) => {
            const parsed = Number(event.target.value)
            emit(Number.isNaN(parsed) ? 0 : parsed, direction)
          }}
        />
      </div>
      <div className="grid gap-2">
        <Label htmlFor={directionId}>Relative to</Label>
        <Select
          value={direction}
          onValueChange={(next) => {
            setDirection(next as Direction)
            emit(amount, next as Direction)
          }}
        >
          <SelectTrigger id={directionId}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="before">Before the event start</SelectItem>
            <SelectItem value="after">After the event start</SelectItem>
          </SelectContent>
        </Select>
      </div>
    </div>
  )
}
